import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { startHeroStatsAnimation } from "../index.js";

import {
  Handshake,
  Dices,
  Ticket,
  Shuffle,
  Trophy,
} from "lucide-react";

const HeroStats = ({
  bookies,
  lotteries,
  buytotal,
  draws,
  wins,
  startAnimation,
}) => {
  const [displayValues, setDisplayValues] = useState([
    0,
    0,
    0,
    0,
    0,
  ]);

  const stats = [
    {
      title: "Creators",
      value: bookies,
      icon: Handshake,
    },
    {
      title: "Lotteries",
      value: lotteries,
      icon: Dices,
    },
    {
      title: "Tickets Sold",
      value: buytotal,
      icon: Ticket,
    },
    {
      title: "Draws",
      value: draws,
      icon: Shuffle,
    },
    {
      title: "Wins",
      value: wins,
      icon: Trophy,
    },
  ];

  useEffect(() => {
    if (!startAnimation) {
      setDisplayValues([
        Number(bookies) || 0,
        Number(lotteries) || 0,
        Number(buytotal) || 0,
        Number(draws) || 0,
        Number(wins) || 0,
      ]);

      return;
    }

    const cleanup = startHeroStatsAnimation(
      stats,
      setDisplayValues
    );

    return cleanup;
  }, [
    startAnimation,
    bookies,
    lotteries,
    buytotal,
    draws,
    wins,
  ]);

  return (
    <Box
      className="home-hero-stats"
      sx={{
        display: {
          xs: "grid",
          sm: "flex",
        },
        gridTemplateColumns: {
          xs: "repeat(5, minmax(0, 1fr))",
        },
        flexDirection: {
          sm: "row",
        },
        justifyContent: {
          sm: "flex-start",
        },
        alignItems: {
          sm: "center",
        },
        gap: {
          xs: (theme) =>
            theme.custom.layoutSizes.heroStats.mobile.gap,
          sm: 0,
        },
        width: {
          xs: "100%",
          sm: "fit-content",
        },
        maxWidth: {
          xs: "100%",
          sm: "100%",
        },
      }}
    >
      {stats.map((stat, index) => {
        const Icon = stat.icon;

        return (
          <Box
            key={stat.title}
            className="hero-stat-item"
            sx={{
              gridColumn: "auto",
              position: "relative",
              minWidth: 0,
              minHeight: {
                xs: (theme) => theme.custom.layoutSizes.heroStats.mobile.cardMinHeight,
                sm: "auto",
              },
              pl: {
                xs: (theme) => theme.custom.layoutSizes.heroStats.mobile.cardPadding,
                sm: "14px",
                md: "18px",
              },
              pr: {
                xs: (theme) => theme.custom.layoutSizes.heroStats.mobile.cardPadding,
                sm: "24px",
                md: "32px",
              },
              py: {
                xs: (theme) => theme.custom.layoutSizes.heroStats.mobile.cardPadding,
                sm: "12px",
                md: "16px",
              },
              borderRadius: {
                xs: "0",
                sm: "0",
              },
              background: {
                xs: "transparent",
                sm: "transparent",
              },
              border: {
                xs: "none",
                sm: "none",
              },
              borderRight: {
                xs: "none",
                sm:
                  index !== stats.length - 1
                    ? "1px solid rgba(133, 143, 163, 0.25)"
                    : "none",
              },
              boxShadow: {
                xs: "none",
                sm: "none",
              },
              backdropFilter: {
                xs: "blur(12px)",
                sm: "none",
              },
              display: "flex",
              flexDirection: {
                xs: "column",
                sm: "row",
              },
              alignItems: "center",
              justifyContent: {
                xs: "center",
                sm: "flex-start",
              },
              textAlign: {
                xs: "center",
                sm: "left",
              },
              gap: {
                xs: "4px",
                sm: "0px",
              },
              overflow: "hidden",
              transition: {
                xs: "transform 0.3s ease, box-shadow 0.3s ease",
                sm: "none",
              },
              "&:hover": {
                transform: {
                  xs: "translateY(-4px)",
                  sm: "none",
                },
                boxShadow: {
                  xs: "0 12px 40px rgba(31, 38, 135, 0.1)",
                  sm: "none",
                },
              },
              "&::after": {
                content: {
                  xs: "none",
                  sm: "none",
                },
              },
            }}
          >
            <Box
              sx={{
                width: {
                  xs: (theme) => theme.custom.layoutSizes.heroStats.mobile.iconBoxSize,
                  sm: "auto",
                },
                height: {
                  xs: (theme) => theme.custom.layoutSizes.heroStats.mobile.iconBoxSize,
                  sm: "auto",
                },
                borderRadius: {
                  xs: "8px",
                  sm: "0",
                },
                background: {
                  xs: "transparent",
                  sm: "transparent",
                },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: {
                  xs: "4px",
                  sm: "0",
                },
                mr: {
                  xs: "0px",
                  sm: "8px",
                  md: "10px",
                },
                flexShrink: 0,
                color: {
                  xs: (theme) => theme.palette.primary.main,
                  sm: (theme) => theme.palette.primary.main,
                },
                "& svg": {
                  width: {
                    xs: (theme) => theme.custom.layoutSizes.heroStats.mobile.iconSize,
                    sm: "22px",
                    md: "26px",
                  },
                  height: {
                    xs: (theme) => theme.custom.layoutSizes.heroStats.mobile.iconSize,
                    sm: "22px",
                    md: "26px",
                  },
                },
              }}
            >
              <Icon strokeWidth={1.8} />
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: {
                  xs: "center",
                  sm: "flex-start",
                },
              }}
            >
              <Typography
                component="span"
                sx={{
                  fontFamily: "inherit",
                  fontSize: {
                    xs: "18px",
                    sm: "18px",
                    md: "22px",
                  },
                  fontWeight: {
                    xs: 800,
                    sm: 800,
                  },
                  color: {
                    xs: (theme) => theme.palette.text.primary,
                    sm: (theme) => theme.palette.text.primary,
                  },
                  lineHeight: 1,
                  order: {
                    xs: 1,
                    sm: 1,
                  },
                  marginTop: {
                    xs: "0",
                    sm: "0",
                  },
                  marginBottom: {
                    xs: "0px",
                    sm: "2px",
                  },
                }}
              >
                {displayValues[index]}
              </Typography>

              <Typography
                component="p"
                sx={{
                  margin: 0,
                  fontFamily: "inherit",
                  fontSize: {
                    xs: "10px",
                    sm: "10px",
                    md: "12px",
                  },
                  fontWeight: {
                    xs: 600,
                    sm: 500,
                  },
                  color: {
                    xs: (theme) => theme.palette.text.secondary,
                    sm: (theme) => theme.palette.text.secondary,
                  },
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                  order: {
                    xs: 2,
                    sm: 2,
                  },
                  letterSpacing: {
                    xs: "0px",
                    sm: "normal",
                  },
                }}
              >
                {stat.title}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default HeroStats;